// ── Support Center Constants ─────────────────────────────────────────────────

export const supportLabel = {
  PAGE_TITLE: "Support Center",
  PAGE_SUBTITLE_ALL: "Track and manage your support requests",

  // Section labels
  EMPLOYEE_SUPPORT: "Employee Support",
  PRODUCT_SUPPORT: "HRFusion Product Support",
  EMPLOYEE_SUPPORT_DESC: "HR & organization-level queries assigned to your HR/Admin team",
  PRODUCT_SUPPORT_DESC: "App issues & feature requests handled by the HRFusion support team",

  // Ticket actions
  NEW_TICKET: "New Ticket",
  ADD_TICKET: "Raise a Ticket",
  EDIT_TICKET: "Edit Ticket",
  VIEW_TICKET: "View Ticket",
  DELETE_TICKET: "Delete Ticket",
  CLOSE_TICKET: "Close Ticket",
  REOPEN_TICKET: "Reopen Ticket",

  // Table headers
  SR_NO: "Sr No.",
  TICKET_ID: "Ticket ID",
  SUBJECT: "Subject",
  CATEGORY: "Category",
  PRIORITY: "Priority",
  STATUS: "Status",
  SUPPORT_TYPE: "Support Type",
  RAISED_BY: "Raised By",
  ASSIGNED_TO: "Assigned To",
  CREATED_AT: "Created At",
  UPDATED_AT: "Last Updated",
  ACTION: "Action",

  // Form fields
  SUPPORT_TYPE_LABEL: "Support Type",
  CATEGORY_LABEL: "Category",
  SUBJECT_LABEL: "Subject",
  DESCRIPTION_LABEL: "Description",
  PRIORITY_LABEL: "Priority",
  STATUS_LABEL: "Status",
  ATTACHMENT_LABEL: "Attachment (Optional)",

  // Modal titles
  RAISE_TICKET_TITLE: "Raise a Support Ticket",
  DELETE_CONFIRM_MSG: "Are you sure you want to delete this ticket? This action cannot be undone.",

  // Empty states
  NO_TICKETS: "No tickets found",
  NO_TICKETS_DESC: "All clear! No support tickets at the moment.",
  CREATE_FIRST: "Raise your first ticket to get help from the support team.",

  // Info banner
  HOW_IT_WORKS_TITLE: "How Support Works",
  HOW_IT_WORKS_DESC:
    "Employee Support tickets are assigned to your HR/Admin team for organization-level queries. Product Support tickets are forwarded to the HRFusion team for app-related issues.",
};

// ── Support Type ─────────────────────────────────────────────────────────────
export const SUPPORT_TYPES = [
  { value: "employee", label: "Employee Support" },
  { value: "product", label: "Product Support" },
];

// ── Categories per support type ──────────────────────────────────────────────
export const EMPLOYEE_CATEGORIES = [
  { value: "attendance", label: "Attendance" },
  { value: "leave", label: "Leave" },
  { value: "payroll", label: "Payroll" },
  { value: "shift", label: "Shift" },
  { value: "employee_profile", label: "Employee Profile" },
  { value: "face_recognition", label: "Face Recognition" },
  { value: "general_hr_query", label: "General HR Query" },
];

export const PRODUCT_CATEGORIES = [
  { value: "application_issue", label: "Application Issue" },
  { value: "bug_report", label: "Bug Report" },
  { value: "feature_request", label: "Feature Request" },
  { value: "integration_issue", label: "Integration Issue" },
  { value: "performance_issue", label: "Performance Issue" },
];

// ── Priority options ──────────────────────────────────────────────────────────
export const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

// ── Status options ────────────────────────────────────────────────────────────
export const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

// ── Status badge config ───────────────────────────────────────────────────────
export const getStatusBadgeStyle = (status) => {
  switch (status?.toLowerCase()) {
    case "open":
      return { bg: "rgba(99,102,241,0.12)", color: "#6366f1" };
    case "in_progress":
      return { bg: "rgba(245,158,11,0.12)", color: "#f59e0b" };
    case "resolved":
      return { bg: "rgba(16,185,129,0.12)", color: "#10b981" };
    case "closed":
      return { bg: "rgba(100,116,139,0.12)", color: "#64748b" };
    default:
      return { bg: "rgba(100,116,139,0.12)", color: "#64748b" };
  }
};

// ── Priority badge config ─────────────────────────────────────────────────────
export const getPriorityBadgeStyle = (priority) => {
  switch (priority?.toLowerCase()) {
    case "low":
      return { bg: "rgba(16,185,129,0.12)", color: "#10b981" };
    case "medium":
      return { bg: "rgba(245,158,11,0.12)", color: "#f59e0b" };
    case "high":
      return { bg: "rgba(249,115,22,0.12)", color: "#f97316" };
    case "critical":
      return { bg: "rgba(244,63,94,0.12)", color: "#f43f5e" };
    default:
      return { bg: "rgba(100,116,139,0.12)", color: "#64748b" };
  }
};

// ── Support type badge config ─────────────────────────────────────────────────
export const getSupportTypeBadgeStyle = (type) => {
  switch (type?.toLowerCase()) {
    case "employee":
      return { bg: "rgba(99,102,241,0.12)", color: "#6366f1" };
    case "product":
      return { bg: "rgba(139,92,246,0.12)", color: "#8b5cf6" };
    default:
      return { bg: "rgba(100,116,139,0.12)", color: "#64748b" };
  }
};

// ── Accessor keys ─────────────────────────────────────────────────────────────
export const SUPPORT_ACCESSOR_KEYS = {
  SR_NO: "sr_no",
  TICKET_NO: "ticket_no",
  SUPPORT_TYPE: "support_type",
  CATEGORY: "category",
  SUBJECT: "subject",
  PRIORITY: "priority",
  STATUS: "status",
  CREATED_AT: "createdAt",
  ACTION: "action",
};
